import lodashSnakeCase from 'lodash-es/snakeCase';

import { eventTypeStandard, getPageType, push } from './shared';

export type UseTrackingGeneralReturn = {
    trackPageView: VoidFunction;
    trackLoginSignupButton: VoidFunction;
    trackMenuDirectSale: VoidFunction;
    trackMenuAuction: VoidFunction;
    trackMenuSell: VoidFunction;
    trackMenuHowToHowSellUsedBus: VoidFunction;
    trackMenuReasonsBuyBusAuction: VoidFunction;
    trackMenuHowToHowGuideBuyUsedBus: VoidFunction;
    trackMenuHowToTrustReport: VoidFunction;
    trackMenuFaq: VoidFunction;
    trackOpenAccountMenu: VoidFunction;
    trackAccountBids: VoidFunction;
    trackAccountYourAuctions: VoidFunction;
    trackAccountYourDirectSales: VoidFunction;
    trackAccountYourOrdersInDirectSales: VoidFunction;
    trackAccountProfile: VoidFunction;
    trackLogout: VoidFunction;
    trackLogState: VoidFunction;
    updateTrackingCompanyMetrics: VoidFunction;
    trackEmailLink: (email: string) => void;
    trackPhoneLink: (phone: string) => void;
};

export const useTrackingGeneral = (): UseTrackingGeneralReturn => {
    const route = useRoute();
    const error = useError();
    const { isLoggedIn, data: session, getSession } = useAuthUtils();

    return {
        trackPageView(): void {
            const locale = useNuxtApp().$i18n.locale; // Cannot use useI18n because it's called in incompatible contexts (e.g. tracking plugin)

            push({
                event: 'page_view',
                page_type: getPageType(),
                page_path: route.fullPath,
                http_status_code: error.value?.status ?? 200,
                lang: locale.value,
                ...eventTypeStandard,
            });
        },

        trackLoginSignupButton(): void {
            push({
                event: 'click',
                click_element: 'button',
                click_text: 'login_signup',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackMenuDirectSale(): void {
            push({
                event: 'click',
                click_element: 'link',
                click_text: 'direct-sale',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackMenuAuction(): void {
            push({
                event: 'click',
                click_element: 'link',
                click_text: 'auction',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackMenuSell(): void {
            push({
                event: 'click',
                click_element: 'link',
                click_text: 'sell',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackMenuHowToHowSellUsedBus(): void {
            push({
                event: 'click',
                click_element: 'link',
                click_text: 'how-to-how-sell-used-bus',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackMenuReasonsBuyBusAuction(): void {
            push({
                event: 'click',
                click_element: 'link',
                click_text: 'how-to-reasons-buy-bus-auction',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackMenuHowToHowGuideBuyUsedBus(): void {
            push({
                event: 'click',
                click_element: 'link',
                click_text: 'how-to-guide-buy-used-bus',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackMenuHowToTrustReport(): void {
            push({
                event: 'click',
                click_element: 'link',
                click_text: 'how-to-trust-report',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackMenuFaq(): void {
            push({
                event: 'click',
                click_element: 'link',
                click_text: 'faq',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackOpenAccountMenu(): void {
            push({
                event: 'click',
                click_element: 'icon',
                click_text: 'account',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackAccountBids(): void {
            push({
                event: 'click',
                click_element: 'button',
                click_text: 'your_bids',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackAccountYourAuctions(): void {
            push({
                event: 'click',
                click_element: 'button',
                click_text: 'your_vehicles_auction',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackAccountYourDirectSales(): void {
            push({
                event: 'click',
                click_element: 'button',
                click_text: 'your_vehicles_buy_now',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackAccountYourOrdersInDirectSales(): void {
            push({
                event: 'click',
                click_element: 'button',
                click_text: 'your_orders_buy_now',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackAccountProfile(): void {
            push({
                event: 'click',
                click_element: 'button',
                click_text: 'your_profile',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackLogout(): void {
            push({
                event: 'click',
                click_element: 'button',
                click_text: 'logout',
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackEmailLink(email): void {
            push({
                event: 'click',
                click_element: 'email',
                click_text: email,
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackPhoneLink(phone): void {
            push({
                event: 'click',
                click_element: 'phone',
                click_text: phone,
                page_type: getPageType(),
                ...eventTypeStandard,
            });
        },

        trackLogState(): void {
            push(
                {
                    event: 'log_state',
                    page_type: getPageType(),
                    ...eventTypeStandard,
                    ...(isLoggedIn.value && session.value && session.value.user
                        ? {
                              user_id: session.value.id,
                              user_email: session.value.user.email,
                              user_status: 'logged',
                              user_info: {
                                  first_name: session.value.user.firstname,
                                  last_name: session.value.user.lastname,
                                  user_phone: session.value.user.phone,
                              },
                              company_info: {
                                  company_name: session.value.user.company?.businessName,
                                  company_id: session.value.user.companyId,
                                  vat: session.value.user.company?.tin,
                                  address: session.value.user.company?.address,
                                  city: session.value.user.company?.city,
                                  region: session.value.user.company?.province,
                                  country: session.value.user.company?.countryCode,
                                  zip_code: session.value.user.company?.postCode,
                                  operator_type: lodashSnakeCase(session.value.user.company?.operatorType) || null,
                                  total_auctions_won: session.value.companyMetrics?.totalAuctionsWon,
                                  total_auctions_joined: session.value.companyMetrics?.totalAuctionsJoined,
                                  total_bids_made: session.value.companyMetrics?.totalBidsMade,
                                  total_auctions_spent: session.value.companyMetrics?.totalAuctionsSpent,
                                  total_direct_sales_made: session.value.companyMetrics?.totalDirectSalesMade,
                                  total_direct_sales_spent: session.value.companyMetrics?.totalDirectSalesSpent,
                              },
                          }
                        : {
                              user_status: 'unlogged',
                          }),
                },
                false
            );
        },

        /**
         * There isn't a strict/authoritative mechanism yet to update counters in `companyMetrics`
         * (e.g., no backend-driven notifications). This function is just a simple wrapper around
         * `getSession` with a more purpose-revealing name.
         *
         * The implementation is best-effort by design: it relies on triggers from primary events
         * that modify the counters and does not synchronize updated session state with event tracking.
         * In practice, we accept that counters may be only approximately up to date.
         *
         * Side note: implementing a strict update mechanism would not be cost-effective at this time.
         */
        updateTrackingCompanyMetrics(): void {
            isLoggedIn.value && getSession();
        },
    };
};
